/* eslint-disable @typescript-eslint/no-explicit-any */
import { nextTick } from 'vue'
import { createI18n as createBaseI18n, type I18n } from 'vue-i18n'

import { LocaleService } from '@/helpers/LocaleService'

const numberFormats: any = {
  en: {
    currency: {
      style: 'currency',
      currency: 'EUR',
      notation: 'standard',
    },
    decimal: {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    },
    percent: {
      style: 'percent',
      useGrouping: false,
    },
  },
  nl: {
    currency: {
      style: 'currency',
      currency: 'EUR',
      useGrouping: true,
      currencyDisplay: 'symbol',
    },
    decimal: {
      style: 'decimal',
      minimumSignificantDigits: 2,
      maximumSignificantDigits: 2,
    },
    percent: {
      style: 'percent',
      useGrouping: false,
    },
  },
  de: {
    currency: {
      style: 'currency',
      currency: 'EUR',
      useGrouping: true,
      currencyDisplay: 'symbol',
    },
    decimal: {
      style: 'decimal',
      minimumSignificantDigits: 2,
      maximumSignificantDigits: 2,
    },
    percent: {
      style: 'percent',
      useGrouping: false,
    },
  },
}

const datetimeFormats: any = {
  en: {
    short: { year: 'numeric', month: 'short', day: 'numeric' },
    long: {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      weekday: 'short',
      hour: 'numeric',
      minute: 'numeric',
      hour12: false,
    },
  },
  nl: {
    short: {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
    },
    long: {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      weekday: 'short',
      hour: 'numeric',
      minute: 'numeric',
      hour12: false,
    },
  },
  de: {
    short: { year: 'numeric', month: 'numeric', day: 'numeric' },
    long: {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      weekday: 'short',
      hour: 'numeric',
      minute: 'numeric',
      hour12: false,
    },
  },
}

export function initializeI18n(locale: string) {
  i18n = createBaseI18n({
    globalInjection: true,
    legacy: false,
    fallbackWarn: false,
    missingWarn: false,
    fallbackLocale: locale,
    locale: locale,
    numberFormats,
    datetimeFormats,
  })
  return i18n
}

export async function setI18nLocale(locale: string) {
  if (!i18n) {
    console.warn('not loading locale messages, i18n not initialized')
    return
  }

  // load locale messages with dynamic import
  const messages = await import(/* webpackChunkName: "locale-[request]" */ `@/locales/${locale}.json`)

  // set locale and locale message
  i18n.global.setLocaleMessage(locale, messages.default)

  if (typeof i18n.global.locale === 'object') {
    i18n.global.locale.value = locale
  }

  return nextTick()
}

export function globalTranslate(key: string): string {
  if (!i18n) {
    console.warn('Translation attempted before i18n initialized')
    return key
  }

  return i18n.global.t(key)
}

export type i18nOptions = { locale: string }

let i18n: I18n | undefined
